import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Pamjet e aplikacionit për momentet e ditës — rindërtime në HTML të ekraneve
 * reale (jo fotografi), me të dhëna shembull dhe pa asnjë thirrje API:
 *   prezenca   → kioska (KioskScreen.js) + konfirmimi te WorkerScanScreen.js;
 *                paneli live i menaxherit shfaqet te skena 3D e hero-s
 *   pushimet   → detaji i kërkesës te ApprovalInbox.js (Refuzo / Aprovo)
 *   dokumentet → ContractList.js (statusi, dokumenti i nënshkruar)
 *   pagat      → fletëpagesa (payslipPdf.js) dhe dërgimi me email (SalariesList.js)
 * Ngjyrat e statuseve janë ato të app-it (theme.js, StatusChip, LivePresence).
 * Pamja është ilustrim: për lexuesit e ekranit lexohet vetëm `label`.
 */

function Window({ title, tag, children, wide }) {
  return (
    <div className={`nxp-app${wide ? ' is-wide' : ''}`}>
      <div className="nxp-app-side">
        <span className="nxp-app-logo" />
        <span className="is-on" /><span /><span /><span />
      </div>
      <div className="nxp-app-main">
        <div className="nxp-app-top">
          <span className="nxp-app-title">{title}</span>
          <span className="nxp-app-tag">{tag}</span>
        </div>
        <div className="nxp-app-body">{children}</div>
      </div>
    </div>
  );
}

const initials = (name) => name.split(' ').map((p) => p[0]).join('');

function Person({ name, role }) {
  return (
    <span className="nxp-app-person">
      <span className="nxp-app-avatar">{initials(name)}</span>
      <span><b>{name}</b>{role && <small>{role}</small>}</span>
    </span>
  );
}

const Chip = ({ tone, children }) => <span className={`nxp-chip is-${tone}`}>{children}</span>;

function Presence({ s }) {
  // KioskScreen.js: titulli, udhëzimi, QR-ja me kornizë teal dhe shiriti i
  // 10 sekondave majtas; ora dhe data në panelin e errët djathtas. QR-ja
  // kodon vetëm «NEXUMA · DEMO» — nuk është kod i vlefshëm prezence.
  return (
    <div className="nxp-shot-pair">
      <div className="nxp-kiosk">
        <div className="nxp-kiosk-main">
          <b className="nxp-kiosk-title">{s.kioskTitle}</b>
          <span className="nxp-kiosk-hint">{s.kioskHint}</span>
          <span className="nxp-kiosk-qr"><QRCodeSVG value="NEXUMA · DEMO" size={116} level="L" fgColor="#101828" /></span>
          <span className="nxp-kiosk-bar"><i /></span>
          <span className="nxp-kiosk-name">{s.kioskName}</span>
        </div>
        <div className="nxp-kiosk-side">
          <b>07:58</b>
          <span>{s.kioskDate}</span>
          <small>{s.sample}</small>
        </div>
      </div>
      <div className="nxp-phone">
        <div className="nxp-phone-done">
          <span className="nxp-phone-check" />
          <b>{s.recorded}</b>
          <span>{s.recordedBody}</span>
          <small>{s.startWork} · 07:58</small>
        </div>
      </div>
    </div>
  );
}

function Leave({ s }) {
  return (
    <Window title={s.requests} tag={s.sample}>
      <div className="nxp-app-req">
        <div className="nxp-app-req-head">
          <b>{s.annualLeave}</b>
          <Chip tone="pending">{s.forApproval}</Chip>
        </div>
        <dl className="nxp-app-fields">
          <div><dt>{s.employee}</dt><dd><Person name="Elira Shahini" /></dd></div>
          <div><dt>{s.period}</dt><dd>02.11 – 04.11.2026</dd></div>
          <div><dt>{s.days}</dt><dd>3</dd></div>
        </dl>
        <div className="nxp-app-balance">
          <span>{s.annualLeave}</span><b>{s.daysLeft(12)}</b>
          <span className="nxp-app-bar"><i style={{ width: '40%' }} /></span>
        </div>
        <div className="nxp-app-actions">
          <span className="nxp-app-btn is-reject">{s.reject}</span>
          <span className="nxp-app-btn is-approve">{s.approve}</span>
        </div>
      </div>
    </Window>
  );
}

function Documents({ s }) {
  const rows = [
    { name: 'Drita Morina', type: s.contractTypes[0], st: <Chip tone="success">{s.active}</Chip>, doc: true },
    { name: 'Arben Krasniqi', type: s.contractTypes[1], st: <Chip tone="warning">{s.expires(12)}</Chip>, doc: true },
    { name: 'Leart Bytyqi', type: s.contractTypes[2], st: <Chip tone="success">{s.active}</Chip>, doc: true, isNew: true },
  ];
  return (
    <Window title={s.contracts} tag={s.sample}>
      <table className="nxp-app-table">
        <thead><tr><th>{s.employee}</th><th className="nxp-hide-xs">{s.contractType}</th><th>{s.colStatus}</th><th>{s.signed}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className={r.isNew ? 'is-new' : ''}>
              <td><Person name={r.name} /></td>
              <td className="nxp-hide-xs">{r.type}</td>
              <td>{r.st}</td>
              <td><span className="nxp-app-doc">{s.uploaded}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Window>
  );
}

function Payslip({ s }) {
  // Shembull: bruto 800 €, pension 5 %, tatimi sipas shkallëve 0 / 8 / 10 % (250 / 450 €).
  return (
    <Window title={s.payroll} tag={s.sample}>
      <div className="nxp-app-slip">
        <div className="nxp-app-slip-head">
          <b>{s.payslipTitle}</b>
          <span>{s.payslipPeriod}</span>
        </div>
        <div className="nxp-app-slip-who"><Person name="Drita Morina" role={s.roles[2]} /></div>
        <dl className="nxp-app-slip-lines">
          <div><dt>{s.grossBase}</dt><dd>800,00€</dd></div>
          <div><dt>{s.pension}</dt><dd>−40,00€</dd></div>
          <div><dt>{s.tax}</dt><dd>−47,00€</dd></div>
        </dl>
        <div className="nxp-app-slip-net"><span>{s.net}</span><b>713,00€</b></div>
        <div className="nxp-app-slip-mail">
          <span>{s.mailed}</span>
          <Chip tone="success">{s.sent}</Chip>
        </div>
      </div>
    </Window>
  );
}

const SCREENS = { prezenca: Presence, pushimet: Leave, dokumentet: Documents, pagat: Payslip };

export default function AppScreen({ id, s }) {
  const Screen = SCREENS[id];
  if (!Screen) return null;
  return (
    <figure className={`nxp-shot is-${id}`} role="img" aria-label={s.labels[id]}>
      <div aria-hidden="true"><Screen s={s} /></div>
    </figure>
  );
}
